import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'
import { api } from '@/lib/api'
import { isAuthenticated, setAuthState } from '@/lib/auth-store'
import {
  clearPendingReferralCode,
  getPendingReferralCode,
  setPendingReferralCode,
} from '@/lib/referral-store'
import heroCommandCenterGraphic from '@/assets/landing/hero-command-center.svg'
import styles from './signup.module.css'

export const Route = createFileRoute('/signup')({
  beforeLoad: () => {
    if (isAuthenticated()) {
      throw redirect({ to: '/home' })
    }
  },
  component: SignupRoute,
})

const signupSchema = z.object({
  name: z.string().trim().min(1).optional(),
  email: z.string().trim().email(),
  password: z.string().min(8),
  referralCode: z.string().trim().min(1).max(64).optional(),
})

function readInitialReferralCode(): string {
  if (typeof window === 'undefined') {
    return ''
  }

  const params = new URLSearchParams(window.location.search)
  const fromQuery = params.get('ref')?.trim().toUpperCase() ?? ''
  if (fromQuery.length > 0) {
    setPendingReferralCode(fromQuery)
    return fromQuery
  }

  return getPendingReferralCode().trim().toUpperCase()
}

function SignupRoute() {
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [referralCode, setReferralCode] = useState(readInitialReferralCode)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    setErrorMessage(null)

    const parseResult = signupSchema.safeParse({
      name: name.trim().length > 0 ? name : undefined,
      email,
      password,
      referralCode: referralCode.trim().length > 0 ? referralCode : undefined,
    })

    if (!parseResult.success) {
      setErrorMessage('Enter a valid email and a stronger password.')
      return
    }

    setIsSubmitting(true)

    try {
      const response = await api.register({
        email: parseResult.data.email,
        password: parseResult.data.password,
        ...(parseResult.data.name ? { name: parseResult.data.name } : {}),
        ...(parseResult.data.referralCode ? { referralCode: parseResult.data.referralCode } : {}),
      })
      setAuthState(response.user, response.session)
      clearPendingReferralCode()
      await navigate({ to: '/home' })
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Signup failed.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className={styles.layout}>
      <aside className={styles.visualPanel}>
        <p className={styles.kicker}>Start Your System</p>
        <h1 className={styles.visualTitle}>Build your weekly LinkedIn routine in one workspace.</h1>
        <p className={styles.visualBody}>
          Create in your voice, schedule with confidence, and turn engagement signals into smart
          outreach actions.
        </p>
        <img
          src={heroCommandCenterGraphic}
          alt="Command center visual showing drafts, weekly schedule, and warm lead queue"
          className={styles.visualImage}
          loading="lazy"
        />
        <div className={styles.visualPoints}>
          <p>14-day trial</p>
          <p>No payment setup yet</p>
          <p>Extension-powered sync</p>
        </div>
      </aside>

      <article className={styles.panel}>
        <h2 className={styles.title}>Create your account</h2>
        <p className={styles.subtitle}>No setup wizard. Start drafting in under two minutes.</p>
        {referralCode.trim().length > 0 && (
          <p className={styles.notice}>Friend offer detected. Your invite code is pre-applied.</p>
        )}
        <ul className={styles.benefits}>
          <li>14-day free trial starts immediately at signup.</li>
          <li>Billing setup is temporarily skipped during onboarding.</li>
          <li>Referral milestones unlock up to 100% account discount.</li>
        </ul>

        <form className={styles.form} onSubmit={handleSubmit}>
          <label className={styles.field}>
            <span>Name (optional)</span>
            <input
              type="text"
              value={name}
              autoComplete="name"
              onChange={(event) => setName(event.target.value)}
            />
          </label>

          <label className={styles.field}>
            <span>Email</span>
            <input
              type="email"
              value={email}
              autoComplete="email"
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>

          <label className={styles.field}>
            <span>Password</span>
            <input
              type="password"
              value={password}
              autoComplete="new-password"
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>

          <label className={styles.field}>
            <span>Referral code (optional)</span>
            <input
              type="text"
              value={referralCode}
              autoComplete="off"
              onChange={(event) => setReferralCode(event.target.value.toUpperCase())}
              placeholder="Friend invite code"
            />
          </label>

          {errorMessage && <p className={styles.error}>{errorMessage}</p>}

          <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
            {isSubmitting ? 'Creating account...' : 'Create account'}
          </button>
        </form>

        <p className={styles.footer}>
          Already have an account? <Link to="/login">Sign in</Link>.
        </p>
      </article>
    </section>
  )
}
